import { supabase } from './client'
import {
  RelatedAppInsert,
  RelatedAppUpdate,
  Task,
  TaskAttachment,
  TaskAttachmentInsert,
  TaskInsert,
  TaskNote,
  TaskNoteInsert,
  TaskTimeEntry,
  TaskTimeEntryInsert,
  TaskUpdate,
  ProfileUpdate,
  TaskRow,
  RelatedAppRecord,
  TaskPrerequisite,
  TaskStatusDefaultDaysUpdate,
  TaskQuote,
  TaskQuoteInsert,
} from '../types/database.types'

type TaskRowWithRelation = TaskRow & {
  related_apps: RelatedAppRecord | null
}

const hydrateTask = (task: TaskRowWithRelation): Task => ({
  ...task,
  related_app: task.related_apps?.name || 'Unknown App',
  related_app_record: task.related_apps || null,
})

const normalizeTaskDueDateUpdates = (updates: TaskUpdate): TaskUpdate => {
  if (!['on_hold', 'released_to_prod', 'completed'].includes(updates.status || '')) {
    return updates
  }

  return {
    ...updates,
    projected_completion: null,
    overall_due_date: null,
    status_due_dates: null,
  }
}

const getRelatedAppDefaultPublishReleaseNotes = async (
  relatedAppId: string | null | undefined
): Promise<boolean | null> => {
  if (!relatedAppId) {
    return null
  }

  const { data, error } = await supabase
    .from('related_apps')
    .select('default_publish_release_notes')
    .eq('id', relatedAppId)
    .maybeSingle()

  if (error) {
    console.error('Error fetching related app release-notes default:', error)
    throw error
  }

  return data?.default_publish_release_notes ?? null
}

const resolveTaskInsertWithRelatedAppDefault = async (task: TaskInsert): Promise<TaskInsert> => {
  const defaultPublishReleaseNotes = await getRelatedAppDefaultPublishReleaseNotes(task.related_app_id)
  if (defaultPublishReleaseNotes === null) {
    return task
  }

  return {
    ...task,
    publish_release_notes: defaultPublishReleaseNotes,
  }
}

const resolveTaskUpdateWithRelatedAppDefault = async (updates: TaskUpdate): Promise<TaskUpdate> => {
  const normalizedUpdates = normalizeTaskDueDateUpdates(updates)

  // Respect explicit publish flag updates from UI (inline edits/forms).
  if (Object.prototype.hasOwnProperty.call(normalizedUpdates, 'publish_release_notes')) {
    return normalizedUpdates
  }

  let relatedAppId: string | null = null
  const hasRelatedAppIdUpdate = Object.prototype.hasOwnProperty.call(normalizedUpdates, 'related_app_id')

  if (hasRelatedAppIdUpdate) {
    relatedAppId = normalizedUpdates.related_app_id ?? null
  } else {
    return normalizedUpdates
  }

  const defaultPublishReleaseNotes = await getRelatedAppDefaultPublishReleaseNotes(relatedAppId)
  if (defaultPublishReleaseNotes === null) {
    return normalizedUpdates
  }

  return {
    ...normalizedUpdates,
    publish_release_notes: defaultPublishReleaseNotes,
  }
}

export class TaskConcurrencyError extends Error {
  constructor(message = 'Task was updated by another user. Refresh and retry.') {
    super(message)
    this.name = 'TaskConcurrencyError'
  }
}

export const mutations = {
  // Create task
  createTask: async (task: TaskInsert): Promise<Task> => {
    const taskWithPublishDefault = await resolveTaskInsertWithRelatedAppDefault(task)

    const { data, error } = await supabase
      .from('tasks')
      .insert([taskWithPublishDefault])
      .select('*, related_apps(*)')
      .single()

    if (error) {
      console.error('Error creating task:', error)
      throw error
    }

    if (!data) throw new Error('No data returned from create')
    return hydrateTask(data as TaskRowWithRelation)
  },

  // Update task
  updateTask: async (taskId: string, updates: TaskUpdate): Promise<Task> => {
    const normalizedUpdates = await resolveTaskUpdateWithRelatedAppDefault(updates)

    const { data, error } = await supabase
      .from('tasks')
      .update(normalizedUpdates)
      .eq('id', taskId)
      .select('*, related_apps(*)')
      .single()

    if (error) {
      console.error('Error updating task:', error)
      throw error
    }

    if (!data) throw new Error('No data returned from update')
    return hydrateTask(data as TaskRowWithRelation)
  },

  // Update task with optimistic concurrency guard
  updateTaskWithExpectedUpdatedAt: async (
    taskId: string,
    expectedUpdatedAt: string,
    updates: TaskUpdate
  ): Promise<Task> => {
    const normalizedUpdates = await resolveTaskUpdateWithRelatedAppDefault(updates)

    const { data, error } = await supabase
      .from('tasks')
      .update(normalizedUpdates)
      .eq('id', taskId)
      .eq('updated_at', expectedUpdatedAt)
      .select('*, related_apps(*)')
      .maybeSingle()

    if (error) {
      console.error('Error updating task with concurrency guard:', error)
      throw error
    }

    if (!data) {
      throw new TaskConcurrencyError()
    }

    return hydrateTask(data as TaskRowWithRelation)
  },

  // Delete task
  deleteTask: async (taskId: string): Promise<void> => {
    const { error } = await supabase.from('tasks').delete().eq('id', taskId)

    if (error) {
      console.error('Error deleting task:', error)
      throw error
    }
  },

  createTaskNote: async (note: TaskNoteInsert): Promise<TaskNote> => {
    const { data, error } = await supabase
      .from('task_notes')
      .insert([note])
      .select()
      .single()

    if (error) {
      console.error('Error creating task note:', error)
      throw error
    }

    if (!data) throw new Error('No data returned from create note')
    return data
  },

  createTaskAttachment: async (attachment: TaskAttachmentInsert): Promise<TaskAttachment> => {
    const { data, error } = await supabase
      .from('task_attachments')
      .insert([attachment])
      .select()
      .single()

    if (error) {
      console.error('Error creating task attachment:', error)
      throw error
    }

    if (!data) throw new Error('No data returned from create attachment')
    return data
  },

  createTaskTimeEntry: async (timeEntry: TaskTimeEntryInsert): Promise<TaskTimeEntry> => {
    const { data, error } = await supabase
      .from('task_time_entries')
      .insert([timeEntry])
      .select()
      .single()

    if (error) {
      console.error('Error creating task time entry:', error)
      throw error
    }

    if (!data) throw new Error('No data returned from create time entry')
    return data
  },

  saveTaskQuote: async (quote: TaskQuoteInsert): Promise<TaskQuote> => {
    const { data, error } = await supabase.from('task_quotes').upsert(quote, { onConflict: 'task_id' }).select().single()
    if (error) throw error
    if (!data) throw new Error('No data returned from quote save')
    return data
  },

  updateTaskQuoteStatus: async (quoteId: string, status: TaskQuote['status']): Promise<TaskQuote> => {
    const { data, error } = await supabase.from('task_quotes').update({ status }).eq('id', quoteId).select().single()
    if (error) throw error
    if (!data) throw new Error('No data returned from quote update')
    return data
  },

  deleteTaskNote: async (noteId: string): Promise<void> => {
    const { error } = await supabase.from('task_notes').delete().eq('id', noteId)

    if (error) {
      console.error('Error deleting task note:', error)
      throw error
    }
  },

  // Update profile
  updateProfile: async (userId: string, updates: ProfileUpdate) => {
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', userId)
      .select()
      .single()

    if (error) {
      console.error('Error updating profile:', error)
      throw error
    }

    if (!data) throw new Error('No data returned from update')
    return data
  },

  updateTaskStatusDefaultDays: async (status: string, updates: TaskStatusDefaultDaysUpdate) => {
    const { data, error } = await supabase
      .from('task_status_default_days')
      .update(updates)
      .eq('status', status)
      .select()
      .single()

    if (error) {
      console.error('Error updating task status default days:', error)
      throw error
    }

    if (!data) throw new Error('No data returned from status default update')
    return data
  },

  createRelatedApp: async (app: RelatedAppInsert): Promise<RelatedAppRecord> => {
    const { data, error } = await supabase
      .from('related_apps')
      .insert([app])
      .select()
      .single()

    if (error) {
      console.error('Error creating related app:', error)
      throw error
    }

    if (!data) throw new Error('No data returned from create')
    return data
  },

  updateRelatedApp: async (appId: string, updates: RelatedAppUpdate): Promise<RelatedAppRecord> => {
    const { data, error } = await supabase
      .from('related_apps')
      .update(updates)
      .eq('id', appId)
      .select()
      .single()

    if (error) {
      console.error('Error updating related app:', error)
      throw error
    }

    if (!data) throw new Error('No data returned from update')
    return data
  },

  // Batch update tasks (for Kanban drag-and-drop)
  batchUpdateTasks: async (updates: Array<{ id: string; updates: TaskUpdate }>) => {
    const updatesWithPublishDefault = await Promise.all(
      updates.map(async ({ id, updates: taskUpdates }) => ({
        id,
        updates: await resolveTaskUpdateWithRelatedAppDefault(taskUpdates),
      }))
    )

    const promises = updatesWithPublishDefault.map(({ id, updates: taskUpdates }) =>
      supabase.from('tasks').update(taskUpdates).eq('id', id)
    )

    const results = await Promise.all(promises)

    // Check for errors
    const errors = results.filter((r) => r.error)
    if (errors.length > 0) {
      console.error('Errors in batch update:', errors)
      throw new Error(`${errors.length} tasks failed to update`)
    }
  },

  setTaskPrerequisite: async (taskId: string, prerequisiteTaskId: string | null): Promise<TaskPrerequisite | null> => {
    const { error: deleteError } = await supabase
      .from('task_prerequisites')
      .delete()
      .eq('task_id', taskId)

    if (deleteError) {
      console.error('Error clearing task prerequisites:', deleteError)
      throw deleteError
    }

    if (!prerequisiteTaskId) {
      return null
    }

    const { data, error } = await supabase
      .from('task_prerequisites')
      .insert([{ task_id: taskId, prerequisite_task_id: prerequisiteTaskId }])
      .select()
      .single()

    if (error) {
      console.error('Error creating task prerequisite:', error)
      throw error
    }

    return data
  },

}
